// "Work sitting with us" board + the tickets-tab side rail (design handoff
// §4.4 / §4.5, prototype `Tickets B - Desk board.dc.html`).
//
// The board shows ONLY the three ball-with-us columns. The moving-without-us
// states deliberately get no board — they exist on the heatmap and nowhere
// else, so the room reads them rather than discussing them.

import { Box } from "@mui/material";
import { brainTokens } from "../design/brainTheme";
import type { DeskState, DeskSummary, UrgentTicket } from "../lib/api";
import { AgentPanel } from "./AgentPanel";
import { PriorityPill, AssigneeMonogram } from "./RegisterTable";
import { AgeChip, TriageChip, tokenRgba } from "./DeskHeatmap";
import {
  DESK_BANDS,
  DESK_STATE_LABEL,
  DESK_STATE_TONE,
  customerPressure,
  deskAgentSummary,
  formatAgo,
  isBallWithUs,
  type CustomerPressure,
} from "./deskModel";

const c = brainTokens.color;
const MONO = brainTokens.font.mono;

const TONE_COLOR = {
  danger: c.dangerText,
  warning: c.warnDot,
  teal: c.teal,
  neutral: c.placeholder,
  success: c.successDot,
} as const;

/** Cards shown per column before the "+ n more in Jira" line. */
const CARD_CAP = 6;

export function DeskWorkBoard({
  tickets,
  onOpenTicket,
}: {
  tickets: UrgentTicket[];
  onOpenTicket: (key: string) => void;
}) {
  const states = DESK_BANDS[0].states;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", mb: "12px", flexWrap: "wrap" }}>
        <Box sx={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em", color: c.ink }}>
          Work sitting with us
        </Box>
        <Box sx={{ fontSize: 13.5, color: c.muted }}>
          the three states that need a human — everything else is on the heatmap only
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0,1fr))",
          gap: "14px",
          alignItems: "start",
          "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" },
        }}
      >
        {states.map((state) => (
          <BoardColumn key={state} state={state} tickets={tickets} onOpenTicket={onOpenTicket} />
        ))}
      </Box>
    </Box>
  );
}

function BoardColumn({
  state,
  tickets,
  onOpenTicket,
}: {
  state: DeskState;
  tickets: UrgentTicket[];
  onOpenTicket: (key: string) => void;
}) {
  const inState = tickets
    .filter((t) => t.deskState === state)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || (b.idleDays ?? 0) - (a.idleDays ?? 0));
  const oldest = inState.reduce((m, t) => Math.max(m, t.idleDays ?? 0), 0);
  const shown = inState.slice(0, CARD_CAP);
  const dot = TONE_COLOR[DESK_STATE_TONE[state]];

  return (
    <Box
      sx={{
        background: c.pageBg,
        border: `1px solid ${c.border}`,
        borderTop: `3px solid ${dot}`,
        borderRadius: `${brainTokens.radius.card}px`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ padding: "13px 14px 11px", borderBottom: `1px solid ${c.borderLight3}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box aria-hidden sx={{ width: 9, height: 9, borderRadius: "50%", flex: "none", background: dot }} />
          <Box
            component="span"
            sx={{ fontSize: 14.5, fontWeight: 600, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: c.ink }}
          >
            {DESK_STATE_LABEL[state]}
          </Box>
          <Box component="span" sx={{ fontFamily: MONO, fontSize: 15, color: c.ink2 }}>
            {inState.length}
          </Box>
        </Box>
        <Box
          sx={{
            fontSize: 12,
            mt: "5px",
            color: oldest > 7 ? c.dangerText : c.muted2,
            fontWeight: oldest > 7 ? 600 : 400,
          }}
        >
          {inState.length ? `oldest ${oldest}d` : "—"}
        </Box>
      </Box>

      <Box sx={{ padding: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
        {shown.map((t) => (
          <Box
            key={t.key}
            role="button"
            tabIndex={0}
            aria-label={`Open ${t.key}`}
            onClick={() => onOpenTicket(t.key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenTicket(t.key);
              }
            }}
            sx={{
              background: c.pageBg,
              border: `1px solid ${c.borderLight}`,
              borderRadius: `${brainTokens.radius.button}px`,
              padding: "10px 11px 11px",
              cursor: "pointer",
              transition: "background 0.12s ease",
              "&:hover": { background: c.panelBg2 },
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <PriorityPill priority={t.bucket} />
              <Box
                component="a"
                href={t.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ fontFamily: MONO, fontSize: 11, whiteSpace: "nowrap", color: c.ink4, textDecoration: "none", "&:hover": { color: c.teal, textDecoration: "underline" } }}
              >
                {t.key.replace(/^TICKET-/, "#")}
              </Box>
              <Box sx={{ marginLeft: "auto" }}>
                <AgeChip days={t.idleDays ?? 0} small />
              </Box>
            </Box>
            <Box sx={{ fontSize: 13.5, lineHeight: 1.4, mt: "8px", color: c.ink }}>{t.summary}</Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "9px" }}>
              <Box
                component="span"
                sx={{ fontSize: 12, color: c.muted, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {t.customer ?? t.account ?? "—"}
              </Box>
              <AssigneeMonogram name={t.assigneeName} size={22} />
            </Box>
            {/* The AI chip gets its own line on a card: inline it competed with
                the customer name for a ~300px column and won, ellipsing the
                operator down to "heli…". Whose ticket it is outranks what the
                model thinks about it. */}
            {t.triage && (
              <Box sx={{ mt: "8px" }}>
                <TriageChip ticket={t} />
              </Box>
            )}
          </Box>
        ))}
        {!inState.length && (
          <Box sx={{ padding: "18px 10px", textAlign: "center", fontSize: 12.5, color: c.faint }}>Clear</Box>
        )}
        {inState.length > shown.length && (
          <Box sx={{ padding: "8px 4px 2px", fontSize: 12.5, color: c.muted2 }}>
            + {inState.length - shown.length} more in Jira
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function DeskSideRail({
  tickets,
  desk,
  fetchedAt,
  activeCustomer,
  onPivotToCustomer,
}: {
  tickets: UrgentTicket[];
  desk?: DeskSummary;
  fetchedAt?: number;
  activeCustomer?: string | null;
  onPivotToCustomer: (customer: string) => void;
}) {
  const pressure = customerPressure(tickets);
  const agentText = deskAgentSummary(tickets, desk ?? null);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box
        sx={{
          background: c.pageBg,
          border: `1px solid ${c.border}`,
          borderRadius: `${brainTokens.radius.card}px`,
          overflow: "hidden",
        }}
      >
        <Box sx={{ padding: "16px 18px 12px" }}>
          <Box sx={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.01em", color: c.ink }}>
            Who is having a bad week
          </Box>
          <Box sx={{ fontSize: 13, color: c.muted, mt: "3px" }}>Longest silence first</Box>
        </Box>
        {pressure.length === 0 && (
          <Box sx={{ padding: "0 18px 16px", fontSize: 13, color: c.faint }}>
            No customer is waiting on us.
          </Box>
        )}
        {pressure.map((p) => (
          <CustomerRow
            key={p.name}
            pressure={p}
            active={activeCustomer === p.name}
            onClick={() => onPivotToCustomer(p.name)}
          />
        ))}
      </Box>

      <AgentPanel context="desk triage" stamp={agentText ? formatAgo(fetchedAt) : undefined}>
        {agentText ?? "Nothing is sitting with us right now — the agent has no ticket to name."}
      </AgentPanel>

      <Box
        sx={{
          background: c.pageBg,
          border: `1px solid ${c.border}`,
          borderRadius: `${brainTokens.radius.card}px`,
          padding: "16px 18px",
        }}
      >
        <Box
          component="span"
          sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: c.faint }}
        >
          How to read it
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "9px", mt: "11px" }}>
          {[
            {
              swatch: { background: tokenRgba(c.dangerText, 0.85), border: "none" },
              text: "Red cells are tickets waiting on us. The further right, the longer since anyone touched them — anything dark in the right-hand columns is an apology in the making.",
            },
            {
              swatch: { background: tokenRgba(c.teal, 0.32), border: "none" },
              text: "Teal cells are moving without the room — development, customers, closures. Read them, do not discuss them.",
            },
            {
              swatch: { background: c.panelBg, border: `1px solid ${c.border}` },
              text: "Empty is the goal. The last column of the top band should be blank by Friday.",
            },
            // The room must never be able to mistake a model's opinion for a
            // decision someone made. Every ✦ chip is labelled AI in place; this
            // is the long-form of the same sentence.
            {
              swatch: { background: c.tealTint2, border: `1px solid ${c.tealTintBorder}` },
              text: "✦ AI chips are the triage agent's suggestion with its confidence — not a decision, and not something anyone has agreed to. Open the ticket to read its reasoning.",
            },
          ].map((l, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <Box aria-hidden sx={{ width: 14, height: 14, borderRadius: "4px", flex: "none", mt: "2px", ...l.swatch }} />
              <Box component="span" sx={{ fontSize: 13, color: c.ink3, lineHeight: 1.5 }}>
                {l.text}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CustomerRow({
  pressure,
  active,
  onClick,
}: {
  pressure: CustomerPressure;
  active: boolean;
  onClick: () => void;
}) {
  const tone =
    pressure.worstIdleDays > 14
      ? { bg: c.dangerBg, fg: c.dangerText }
      : pressure.worstIdleDays > 7
        ? { bg: c.warnBg, fg: c.warnText }
        : { bg: c.neutralPillBg, fg: c.muted };
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Pivot the heatmap to ${pressure.name}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "11px",
        padding: "11px 18px",
        borderTop: `1px solid ${c.borderLight3}`,
        cursor: "pointer",
        background: active ? c.tealTint2 : "transparent",
        "&:hover": { background: active ? c.tealTint2 : c.panelBg2 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          borderRadius: `${brainTokens.radius.card}px`,
          background: c.tealTint,
          color: c.tealInk,
          fontFamily: MONO,
          fontSize: 11,
          fontWeight: 700,
          flex: "none",
        }}
      >
        {pressure.monogram}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: c.ink }}>
          {pressure.name}
        </Box>
        <Box sx={{ fontSize: 12.5, color: c.muted, mt: "2px" }}>
          {pressure.open} open · {pressure.withUs} with us
        </Box>
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: 11.5,
          fontWeight: 700,
          padding: "4px 9px",
          borderRadius: `${brainTokens.radius.pill}px`,
          whiteSpace: "nowrap",
          background: tone.bg,
          color: tone.fg,
        }}
      >
        {pressure.worstIdleDays}d
      </Box>
    </Box>
  );
}

/** Exported for the tab's empty state. */
export function noBallWithUs(tickets: UrgentTicket[]): boolean {
  return !tickets.some((t) => t.deskState && isBallWithUs(t.deskState));
}
